import { useEffect, useSyncExternalStore } from 'react';
import { Button, Dialog, Input } from './index';
import { useT } from '@/i18n';

/**
 * Eigene Bestätigungs- und Hinweisdialoge statt `window.confirm` / `alert` /
 * `prompt`.
 *
 * Die Browser-Fenster waren an 29 Stellen im Einsatz: am Handy blockierend,
 * nicht gestaltbar, mit „OK / Abbrechen" in der Sprache des Browsers statt der
 * App — und `prompt` als Kopier-Ersatz auf iOS über http (also am Turniertag)
 * nicht einmal markierbar. Hier: ein Blatt von unten am Handy, ein Fenster am
 * Laptop, Knöpfe mit dem Verb der Handlung, Enter bestätigt, Escape bricht ab.
 *
 * Aufruf aus jedem Ereignis-Handler: `if (!(await confirmDialog(t('…')))) return;`
 * Der `DialogHost` hängt einmal in App.tsx.
 */
/** Ein Knopf einer Auswahl (`choiceDialog`): `key` kommt zurück, `danger` färbt rot */
export interface Wahl {
  key: string;
  label: string;
  danger?: boolean;
}

interface Anfrage {
  id: number;
  art: 'confirm' | 'alert' | 'copy' | 'choice';
  text: string;
  titel?: string;
  ok?: string;
  cancel?: string;
  /** rot: etwas geht verloren */
  danger?: boolean;
  /** nur `choice`: die Knöpfe außer „Abbrechen", in dieser Reihenfolge */
  wahl?: Wahl[];
  /** `confirm`/`alert`/`copy`: ja/nein · `choice`: der Schlüssel des Knopfs, `null` = abgebrochen */
  resolve: (r: boolean | string | null) => void;
}

let aktuelle: Anfrage | null = null;
let seq = 0;
const hoerer = new Set<() => void>();
function setze(a: Anfrage | null): void {
  aktuelle = a;
  for (const h of hoerer) h();
}
function antwort(a: Anfrage, r: boolean | string | null): void {
  if (aktuelle?.id !== a.id) return;
  a.resolve(r);
  setze(null);
}
function frage(a: Omit<Anfrage, 'id' | 'resolve'>): Promise<boolean | string | null> {
  // Eine Frage nach der anderen — eine zweite verdrängt die erste mit „nein"
  aktuelle?.resolve(aktuelle.art === 'choice' ? null : false);
  return new Promise((resolve) => setze({ ...a, id: ++seq, resolve }));
}

export function confirmDialog(text: string, opts: { titel?: string; ok?: string; cancel?: string; danger?: boolean } = {}): Promise<boolean> {
  return frage({ art: 'confirm', text, ...opts }).then((r) => r === true);
}
export async function alertDialog(text: string, opts: { titel?: string } = {}): Promise<void> {
  await frage({ art: 'alert', text, ...opts });
}
/** Ersatz für `prompt(…, link)`: der Text steht in einem Feld, markiert, zum Kopieren */
export async function copyDialog(text: string, titel?: string): Promise<void> {
  await frage({ art: 'copy', text, ...(titel ? { titel } : {}) });
}
/**
 * Rückfrage mit mehr als zwei Antworten („Beides löschen" · „Nur aus der Cloud"
 * · Abbrechen). Gibt den `key` des gedrückten Knopfs zurück, `null` bei
 * Abbrechen oder Escape. Enter löst **nur** den Knopf aus, der den Fokus hat —
 * eine Vorgabe gibt es hier nicht, bei drei Wegen soll niemand aus Gewohnheit
 * den falschen nehmen. Der Fokus liegt beim Öffnen auf „Abbrechen".
 */
export function choiceDialog(text: string, opts: { titel?: string; wahl: Wahl[]; cancel?: string }): Promise<string | null> {
  return frage({ art: 'choice', text, ...opts }).then((r) => (typeof r === 'string' ? r : null));
}

export function DialogHost() {
  const t = useT();
  const a = useSyncExternalStore(
    (fn) => {
      hoerer.add(fn);
      return () => hoerer.delete(fn);
    },
    () => aktuelle,
  );
  // Enter bestätigt — aber nicht, wenn gerade in einem Feld getippt wird, und
  // nicht, wenn der Fokus auf einem anderen Knopf liegt: Steht er auf
  // „Abbrechen", muss Enter abbrechen. Vorher bestätigte Enter dort trotzdem —
  // und ein Turnier war gelöscht. Knöpfe und Links lösen Enter selbst aus.
  useEffect(() => {
    if (!a) return;
    // Bei einer Auswahl (`choice`) gibt es keine Vorgabe: Enter wirkt nur über den fokussierten Knopf.
    if (a.art === 'choice') return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Enter' || e.defaultPrevented) return;
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === 'TEXTAREA' || tag === 'BUTTON' || tag === 'A' || tag === 'SELECT') return;
      e.preventDefault();
      antwort(a, true);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [a]);
  if (!a) return null;
  const ok = () => antwort(a, true);
  const nein = () => antwort(a, a.art === 'choice' ? null : false);
  const titel = a.titel ?? (a.art === 'confirm' || a.art === 'choice' ? t('Bitte bestätigen') : a.art === 'copy' ? t('Link kopieren') : t('Hinweis'));
  return (
    <Dialog
      open
      onClose={nein}
      title={titel}
      width="max-w-md"
      footer={
        a.art === 'choice' ? (
          /* Auswahl: Abbrechen links (mit dem Fokus), die Wege rechts; am Handy untereinander.
             Kein `data-dialog="ok"` — die Browserläufe drücken den sonst von selbst. */
          <div className="flex flex-wrap gap-2 w-full justify-end">
            <Button variant="ghost" onClick={nein} data-dialog="cancel" autoFocus className="mr-auto">
              {a.cancel ?? t('Abbrechen')}
            </Button>
            {(a.wahl ?? []).map((w) => (
              <Button key={w.key} variant={w.danger ? 'danger' : 'default'} onClick={() => antwort(a, w.key)} data-dialog={`wahl:${w.key}`}>
                {w.label}
              </Button>
            ))}
          </div>
        ) : (
          <>
            <div className="flex-1" />
            {a.art === 'confirm' && (
              <Button variant="ghost" onClick={nein} data-dialog="cancel">
                {a.cancel ?? t('Abbrechen')}
              </Button>
            )}
            <Button variant={a.danger ? 'danger' : 'primary'} onClick={ok} data-dialog="ok" autoFocus>
              {a.ok ?? (a.art === 'confirm' ? t('Ja') : t('OK'))}
            </Button>
          </>
        )
      }
    >
      {a.art === 'copy' ? (
        <div className="grid gap-2">
          <Input readOnly value={a.text} onFocus={(e) => e.target.select()} className="font-mono text-xs" autoFocus />
          <div className="text-xs text-dim">{t('Feld antippen, alles markieren, kopieren.')}</div>
        </div>
      ) : (
        <p className="m-0 text-sm leading-relaxed whitespace-pre-line">{a.text}</p>
      )}
    </Dialog>
  );
}
